import {ChangeDetectorRef, Component, forwardRef, Input, NgZone, OnDestroy, OnInit} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Address, TripEnd} from '../../../interfaces/trip';
import {PlaceAddress, placesAvailable} from '../../../controls/places-editor/place-address';
import {GoogleMapsService} from '../../../shared/google-maps.service';

const FIELDS = ['street', 'number', 'postcode', 'town'] as const;
// Mirrors TripController.php:226-230.
const LIMITS = {street: 120, number: 20, postcode: 12, town: 80} as const;

export function endReady(end: TripEnd): boolean {
  return !!end && (end.home || FIELDS.every(k => {
    const v = end[k]?.trim();
    return !!v && v.length <= LIMITS[k];
  }));
}

/** One trip end: the saved home address, or an address picked from Google Places. */
@Component({
  selector: 'bst-trip-end',
  templateUrl: './trip-end.component.html',
  styleUrls: ['./trip-end.component.scss'],
  providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TripEndComponent), multi: true}],
  standalone: false
})
export class TripEndComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() legend: string;
  @Input() home: Partial<Address> | null = null;

  end: TripEnd = {home: false};
  // Held, not derived: a fresh object per check would make ngModel rewrite the search field mid-typing.
  search: PlaceAddress | null = null;
  disabled = false;
  private onChange = (end: TripEnd) => {};
  onTouched = () => {};

  constructor(private zone?: NgZone, private cdr?: ChangeDetectorRef, private maps?: GoogleMapsService) {}

  // Typing is always possible: without Google it's the only way in; with Google it's how a
  // partial pick (missing house number/postcode) gets completed by hand (ruling 2026-10-07).
  // A getter, not a snapshot field: a key rejected after construction (gm-auth-failure) must
  // flip this live, not stay stuck on whatever was true when the control was built.
  // Not manual while the Maps script is still loading: the search field is shown, and this
  // component starts the load itself, since in manual mode nothing else would (#1755).
  get manual(): boolean {
    return !placesAvailable() && (this.maps?.settled ?? true);
  }

  // Latched on a partial pick or the escape hatch (not live): typing the last field true would
  // make this go false mid-keystroke and yank the inputs away before a typo can be fixed.
  // Cleared only by the next pick or by Home, never by typing.
  revealed = false;
  // True only when `revealed` came from a partial pick, so the `places.partial` hint isn't
  // shown for the escape hatch or for `manual`.
  partial = false;

  private onAuthFailure = () => { this.zone ? this.zone.run(() => this.cdr?.markForCheck()) : this.cdr?.markForCheck(); };

  ngOnInit() {
    window.addEventListener('gm-auth-failure', this.onAuthFailure);
    const loaded = this.maps?.load();
    // Only a change-detection kick, so `manual` is re-read once the load settles.
    if (loaded && !this.maps.settled) { loaded.then(this.onAuthFailure); }
  }
  ngOnDestroy() { window.removeEventListener('gm-auth-failure', this.onAuthFailure); }

  // Resets revealed/partial before applying the new end: a latch from a previous end must not
  // survive onto an end that was just set wholesale (Codex Minor 3).
  writeValue(end: TripEnd): void {
    this.revealed = false;
    this.partial = false;
    this.end = {...(end ?? {home: false})};
    this.search = (!this.end.home && this.end.street)
      ? {formatted: `${this.end.street} ${this.end.number}, ${this.end.postcode} ${this.end.town}`} as PlaceAddress
      : null;
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; }

  setHome(home: boolean) {
    // Unchecking Home must not keep the copied home address sitting in the fields as if it
    // were a pick (Codex I2): Save stays disabled until a new address is given.
    this.end = home ? {...this.end, home} : {home: false, street: '', number: '', postcode: '', town: ''};
    this.search = null;
    this.revealed = false;
    this.partial = false;
    this.emit();
  }

  // A partial pick (no house number or postcode, or a field over its limit) is accepted as-is;
  // endsReady/endReady, not this control, gates Save until the address is complete. See Task 3
  // rulings and Codex I1 (length limits).
  pick(place: PlaceAddress | null) {
    this.search = place;
    this.end = place ? {home: false, street: place.street, number: place.number, postcode: place.postcode, town: place.town}
                      : {home: false, street: '', number: '', postcode: '', town: ''};
    this.revealed = FIELDS.some(k => {
      const v = this.end[k]?.trim();
      return !v || v.length > LIMITS[k];
    });
    this.partial = this.revealed;
    this.emit();
  }

  // Escape hatch: lets the user open the manual fields without a partial pick, e.g. to fix a
  // wrong house-number addition or to proceed when predictions are denied without an auth
  // failure (ruling 2026-10-07, review I1/I2).
  reveal() {
    this.revealed = true;
    this.partial = false;
  }

  emit() {
    this.onChange({...this.end});
    this.onTouched();
  }
}
